// =====================
// محلل البيانات المحسن للأداء
// =====================

export interface DataField {
  name: string;
  type: string;
  examples: unknown[];
  nullable: boolean;
  uniqueCount: number;
  minLength?: number;
  maxLength?: number;
  avgLength?: number;
  minValue?: number;
  maxValue?: number;
  avgValue?: number;
  percentiles?: {
    p30: unknown;
    p60: unknown;
    p90: unknown;
  };
  enumValues?: unknown[];
  isEnum: boolean;
  isUnique: boolean;
  isId: boolean;
  isRequired: boolean;
  pattern?: string;
  format?: string;
  precision?: number;
  scale?: number;
  currency?: string;
  coordinates?: {
    lat: boolean;
    lng: boolean;
  };
  phone?: {
    international: boolean;
    patterns: string[];
  };
  email?: boolean;
  url?: boolean;
  date?: {
    format: string;
    timezone?: string;
  };
  timestamp?: {
    unit: 'milliseconds' | 'seconds';
    epoch: number;
  };
  objectId?: {
    format: 'mongodb' | 'custom';
  };
  uuid?: {
    format: 'standard' | 'custom';
  };
}

export interface AnalysisResult {
  totalRecords: number;
  fields: DataField[];
  structure: {
    type: 'array' | 'object' | 'mixed';
    nested: boolean;
    nestedObjects: string[];
    relationships: Relationship[];
  };
  statistics: {
    totalFields: number;
    uniqueFields: number;
    enumFields: number;
    idFields: number;
    requiredFields: number;
    nullableFields: number;
  };
  suggestions: {
    primaryKey?: string;
    foreignKeys: string[];
    indexes: string[];
    constraints: string[];
  };
  metadata: {
    source: 'json' | 'csv';
    encoding: string;
    size: number;
    processedAt: Date;
    analysisTime?: number;
  };
}

export interface Relationship {
  from: string;
  to: string;
  type: 'one-to-one' | 'one-to-many' | 'many-to-many';
  strength: number;
}

// =====================
// محلل البيانات المحسن للأداء
// =====================

export class OptimizedDataAnalyzer {
  private data: unknown[];
  private fields: Map<string, DataField> = new Map();
  private relationships: Relationship[] = [];
  private maxSampleSize = 1000; // تحديد حجم العينة للتحليل السريع
  private progressCallback?: (progress: number, stage: string) => void;

  constructor(data: unknown[], progressCallback?: (progress: number, stage: string) => void) {
    this.data = data;
    this.progressCallback = progressCallback;
  }

  async analyze(): Promise<AnalysisResult> {
    const startTime = performance.now();
    
    this.updateProgress(0, 'بدء التحليل...');
    
    // تحليل سريع للحقول الأساسية
    await this.analyzeFields();
    this.updateProgress(50, 'تحليل الحقول مكتمل...');
    
    // تحليل العلاقات (مؤجل للأداء)
    this.analyzeRelationships();
    this.updateProgress(80, 'تحليل العلاقات مكتمل...');
    
    // حساب الإحصائيات
    const statistics = this.calculateStatistics();
    const suggestions = this.generateSuggestions();
    this.updateProgress(90, 'حساب الإحصائيات مكتمل...');
    
    const endTime = performance.now();
    const analysisTime = endTime - startTime;
    
    this.updateProgress(100, 'التحليل مكتمل!');
    console.log(`Data analysis completed in ${analysisTime}ms`);

    return {
      totalRecords: this.data.length,
      fields: Array.from(this.fields.values()),
      structure: this.analyzeStructure(),
      statistics,
      suggestions,
      metadata: {
        source: 'json',
        encoding: 'utf-8',
        size: JSON.stringify(this.data).length,
        processedAt: new Date(),
        analysisTime
      }
    };
  }

  private updateProgress(progress: number, stage: string): void {
    if (this.progressCallback) {
      this.progressCallback(progress, stage);
    }
  }

  private async analyzeFields(): Promise<void> {
    if (this.data.length === 0) return;

    const firstItem = this.data[0] as Record<string, unknown>;
    const fieldNames = Object.keys(firstItem);
    
    // تحليل متوازي للحقول مع تحديث التقدم
    const totalFields = fieldNames.length;
    let completedFields = 0;
    
    const analysisPromises = fieldNames.map(async (fieldName) => {
      await this.analyzeField(fieldName);
      completedFields++;
      const progress = 50 * (completedFields / totalFields);
      this.updateProgress(progress, `تحليل الحقل: ${fieldName}...`);
    });
    
    await Promise.all(analysisPromises);
  }

  private async analyzeField(fieldName: string): Promise<void> {
    // أخذ عينة من البيانات للتحليل السريع
    const sampleSize = Math.min(this.data.length, this.maxSampleSize);
    const sampleData = this.data.slice(0, sampleSize);
    
    const values = sampleData
      .map(item => (item as Record<string, unknown>)[fieldName])
      .filter(value => value !== null && value !== undefined);

    if (values.length === 0) return;

    const field: DataField = {
      name: fieldName,
      type: this.detectType(fieldName, values),
      examples: values.slice(0, 5), // أمثلة محدودة
      nullable: values.length < sampleData.length,
      uniqueCount: new Set(values).size,
      isEnum: false,
      isUnique: false,
      isId: false,
      isRequired: false
    };

    // تحليل محسن للحقل
    await this.enhanceFieldAnalysis(field, values);
    
    this.fields.set(fieldName, field);
  }

  private detectType(fieldName: string, values: unknown[]): string {
    if (values.length === 0) return 'String';

    // تحليل سريع للنوع
    const firstValue = values[0];
    const type = typeof firstValue;

    if (type === 'number') {
      return Number.isInteger(firstValue as number) ? 'Number' : 'Float';
    }
    if (type === 'boolean') return 'Boolean';
    if (type === 'string') {
      const strValue = firstValue as string;
      
      // فحص سريع للأنواع الخاصة
      if (this.isDate(strValue)) return 'Date';
      if (this.isTimestamp(strValue)) return 'Timestamp';
      if (this.isCurrency(strValue)) return 'Currency';
      if (this.isPhone(strValue)) return 'Phone';
      if (this.isBoolean(strValue)) return 'Boolean';
      
      // فحص البريد الإلكتروني والرابط
      if (strValue.includes('@') && strValue.includes('.')) return 'Email';
      if (strValue.startsWith('http')) return 'URL';
      
      return 'String';
    }
    if (Array.isArray(firstValue)) return 'Array';
    if (type === 'object' && firstValue !== null) return 'Object';

    return 'String';
  }

  private async enhanceFieldAnalysis(field: DataField, values: unknown[]): Promise<void> {
    // تحليل محسن للأداء
    const uniqueValues = new Set(values);
    field.isUnique = uniqueValues.size === values.length;
    field.isEnum = uniqueValues.size <= 10 && values.length > 5;
    
    if (field.isEnum) {
      field.enumValues = Array.from(uniqueValues).slice(0, 10);
    }

    // تحليل المعرفات
    field.isId = this.isIdField(field.name, field, values);

    // تحليل إضافي حسب النوع
    switch (field.type) {
      case 'String':
        this.analyzeStringField(field, values);
        break;
      case 'Number':
      case 'Float':
        this.analyzeNumberField(field, values);
        break;
      case 'Array':
        this.analyzeArrayField(field, values);
        break;
      case 'Object':
        this.analyzeObjectField(field, values);
        break;
      case 'Date':
        this.analyzeDateField(field, values);
        break;
      case 'Timestamp':
        this.analyzeTimestampField(field, values);
        break;
      case 'Currency':
        this.analyzeCurrencyField(field, values);
        break;
      case 'Coordinates':
        this.analyzeCoordinatesField(field, values);
        break;
      case 'Phone':
        this.analyzePhoneField(field, values);
        break;
    }
  }

  private analyzeStringField(field: DataField, values: unknown[]): void {
    const stringValues = values.filter(v => typeof v === 'string') as string[];
    if (stringValues.length === 0) return;

    field.minLength = Math.min(...stringValues.map(s => s.length));
    field.maxLength = Math.max(...stringValues.map(s => s.length));
    field.avgLength = Math.round(stringValues.reduce((sum, s) => sum + s.length, 0) / stringValues.length);

    // فحص الأنواع الخاصة
    field.email = stringValues.some(s => s.includes('@') && s.includes('.'));
    field.url = stringValues.some(s => s.startsWith('http'));
    
    // فحص البريد الإلكتروني
    if (field.email) {
      field.email = true;
    }
    
    // فحص الرابط
    if (field.url) {
      field.url = true;
    }
  }

  private analyzeNumberField(field: DataField, values: unknown[]): void {
    const numberValues = values.filter(v => typeof v === 'number') as number[];
    if (numberValues.length === 0) return;

    field.minValue = Math.min(...numberValues);
    field.maxValue = Math.max(...numberValues);
    field.avgValue = numberValues.reduce((sum, n) => sum + n, 0) / numberValues.length;

    // حساب النسب المئوية
    const sorted = [...numberValues].sort((a, b) => a - b);
    field.percentiles = {
      p30: sorted[Math.floor(sorted.length * 0.3)],
      p60: sorted[Math.floor(sorted.length * 0.6)],
      p90: sorted[Math.floor(sorted.length * 0.9)]
    };
  }

  private analyzeArrayField(field: DataField, values: unknown[]): void {
    // تحليل بسيط للمصفوفات
    const arrayValues = values.filter(v => Array.isArray(v));
    if (arrayValues.length > 0) {
      const lengths = arrayValues.map(arr => (arr as unknown[]).length);
      field.minLength = Math.min(...lengths);
      field.maxLength = Math.max(...lengths);
      field.avgLength = Math.round(lengths.reduce((sum, len) => sum + len, 0) / lengths.length);
    }
  }

  private analyzeObjectField(field: DataField, values: unknown[]): void {
    // تحليل بسيط للكائنات
    const objectValues = values.filter(v => typeof v === 'object' && v !== null && !Array.isArray(v));
    if (objectValues.length > 0) {
      // يمكن إضافة تحليل أكثر تفصيلاً هنا
    }
  }

  private analyzeDateField(field: DataField, values: unknown[]): void {
    const dateValues = values.filter(v => typeof v === 'string' && this.isDate(v as string));
    if (dateValues.length > 0) {
      field.date = {
        format: 'ISO',
        timezone: 'UTC'
      };
    }
  }

  private analyzeTimestampField(field: DataField, values: unknown[]): void {
    const timestampValues = values.filter(v => typeof v === 'number' || (typeof v === 'string' && this.isTimestamp(v as string)));
    if (timestampValues.length > 0) {
      field.timestamp = {
        unit: 'milliseconds',
        epoch: Date.now()
      };
    }
  }

  private analyzeCurrencyField(field: DataField, values: unknown[]): void {
    const currencyValues = values.filter(v => typeof v === 'string' && this.isCurrency(v as string));
    if (currencyValues.length > 0) {
      field.currency = 'USD'; // افتراضي
    }
  }

  private analyzeCoordinatesField(field: DataField, values: unknown[]): void {
    // تحليل بسيط للإحداثيات
    field.coordinates = {
      lat: true,
      lng: true
    };
  }

  private analyzePhoneField(field: DataField, values: unknown[]): void {
    const phoneValues = values.filter(v => typeof v === 'string' && this.isPhone(v as string));
    if (phoneValues.length > 0) {
      field.phone = {
        international: true,
        patterns: ['+966']
      };
    }
  }

  private isDate(value: string): boolean {
    return !isNaN(Date.parse(value));
  }

  private isTimestamp(value: string): boolean {
    return !isNaN(Number(value)) && Number(value) > 1000000000;
  }

  private isCurrency(value: string): boolean {
    return /^\$?\d+(\.\d{2})?$/.test(value);
  }

  private isPhone(value: string): boolean {
    return /^\+?[\d\s\-\(\)]+$/.test(value);
  }

  private isBoolean(value: string): boolean {
    return value.toLowerCase() === 'true' || value.toLowerCase() === 'false';
  }

  private isIdField(fieldName: string, field: DataField, values: unknown[]): boolean {
    const name = fieldName.toLowerCase();
    
    // فحص أسماء الحقول الشائعة للمعرفات
    if (name.includes('id') || name.includes('_id') || name === 'id') {
      return true;
    }
    
    // فحص القيم الفريدة
    if (field.isUnique && values.length > 0) {
      return true;
    }
    
    return false;
  }

  private analyzeRelationships(): void {
    // تحليل بسيط للعلاقات
    const fields = Array.from(this.fields.values());
    const idFields = fields.filter(f => f.isId);
    
    for (const idField of idFields) {
      for (const otherField of fields) {
        if (otherField.name !== idField.name && this.hasRelationship(idField, otherField)) {
          this.relationships.push({
            from: idField.name,
            to: otherField.name,
            type: 'one-to-many',
            strength: this.calculateRelationshipStrength(idField, otherField)
          });
        }
      }
    }
  }

  private hasRelationship(field1: DataField, field2: DataField): boolean {
    const name1 = field1.name.toLowerCase();
    const name2 = field2.name.toLowerCase();
    
    return name1.includes('id') && name2.includes('id') && name1 !== name2;
  }

  private calculateRelationshipStrength(field1: DataField, field2: DataField): number {
    // حساب بسيط لقوة العلاقة
    return 0.5; // قيمة افتراضية
  }

  private analyzeStructure() {
    const hasArrays = this.data.some(item => Array.isArray(item));
    const hasObjects = this.data.some(item => typeof item === 'object' && item !== null && !Array.isArray(item));
    
    let type: 'array' | 'object' | 'mixed';
    if (hasArrays && !hasObjects) type = 'array';
    else if (hasObjects && !hasArrays) type = 'object';
    else type = 'mixed';
    
    const nestedObjects = Array.from(this.fields.values())
      .filter(f => f.type === 'Object')
      .map(f => f.name);
    
    return {
      type,
      nested: nestedObjects.length > 0,
      nestedObjects,
      relationships: this.relationships
    };
  }

  private calculateStatistics() {
    const fields = Array.from(this.fields.values());
    
    return {
      totalFields: fields.length,
      uniqueFields: fields.filter(f => f.isUnique).length,
      enumFields: fields.filter(f => f.isEnum).length,
      idFields: fields.filter(f => f.isId).length,
      requiredFields: fields.filter(f => f.isRequired).length,
      nullableFields: fields.filter(f => f.nullable).length
    };
  }

  private generateSuggestions() {
    const fields = Array.from(this.fields.values());
    const idFields = fields.filter(f => f.isId);
    
    return {
      primaryKey: idFields[0]?.name,
      foreignKeys: this.relationships.map(r => r.from),
      indexes: fields.filter(f => f.isUnique || f.isId).map(f => f.name),
      constraints: fields.filter(f => f.isRequired).map(f => `${f.name} NOT NULL`)
    };
  }
}

// =====================
// مولدات الأكواد المحسنة
// =====================

export class OptimizedCodeGenerator {
  private analysis: AnalysisResult;

  constructor(analysis: AnalysisResult) {
    this.analysis = analysis;
  }

  generateTypeScript(): string {
    const properties = this.analysis.fields.map(field => {
      const type = this.mapTypeToTypeScript(field.type);
      const optional = field.nullable ? '?' : '';
      return `  ${field.name}${optional}: ${type};`;
    }).join('\n');

    return `interface DataItem {
${properties}
}

type DataArray = DataItem[];`;
  }

  private mapTypeToTypeScript(type: string): string {
    const typeMap: Record<string, string> = {
      'String': 'string',
      'Number': 'number',
      'Float': 'number',
      'Boolean': 'boolean',
      'Date': 'Date',
      'Timestamp': 'number',
      'ObjectId': 'string',
      'UUID': 'string',
      'ID': 'number',
      'Email': 'string',
      'URL': 'string',
      'Phone': 'string',
      'Currency': 'number',
      'Coordinates': '{ lat: number; lng: number }',
      'Array': 'unknown[]',
      'Object': 'Record<string, unknown>'
    };

    return typeMap[type] || 'unknown';
  }

  generateSQL(): string {
    const columns = this.analysis.fields.map(field => {
      const type = this.mapTypeToSQL(field.type);
      const nullable = field.nullable ? 'NULL' : 'NOT NULL';
      const unique = field.isUnique ? 'UNIQUE' : '';
      const primary = field.isId ? 'PRIMARY KEY' : '';
      
      const constraints = [nullable, unique, primary].filter(Boolean).join(' ');
      return `  ${field.name} ${type}${constraints ? ' ' + constraints : ''}`;
    }).join(',\n');

    return `CREATE TABLE data_table (
${columns}
);`;
  }

  private mapTypeToSQL(type: string): string {
    const typeMap: Record<string, string> = {
      'String': 'TEXT',
      'Number': 'INTEGER',
      'Float': 'REAL',
      'Boolean': 'BOOLEAN',
      'Date': 'DATE',
      'Timestamp': 'BIGINT',
      'ObjectId': 'VARCHAR(24)',
      'UUID': 'VARCHAR(36)',
      'ID': 'INTEGER',
      'Email': 'VARCHAR(255)',
      'URL': 'TEXT',
      'Phone': 'VARCHAR(20)',
      'Currency': 'DECIMAL(10,2)',
      'Coordinates': 'POINT',
      'Array': 'JSON',
      'Object': 'JSON'
    };

    return typeMap[type] || 'TEXT';
  }

  generatePython(): string {
    const fields = this.analysis.fields.map(field => {
      const type = this.mapTypeToPython(field.type);
      const default_value = field.nullable ? ' = None' : '';
      return `    ${field.name}: ${type}${default_value}`;
    }).join('\n');

    return `from dataclasses import dataclass
from typing import Optional, List, Dict, Any

@dataclass
class DataItem:
${fields}
    
    def to_dict(self) -> Dict[str, Any]:
        return {
${this.analysis.fields.map(f => `            '${f.name}': self.${f.name}`).join(',\n')}
        }

DataArray = List[DataItem]`;
  }

  private mapTypeToPython(type: string): string {
    const typeMap: Record<string, string> = {
      'String': 'str',
      'Number': 'int',
      'Float': 'float',
      'Boolean': 'bool',
      'Date': 'str',
      'Timestamp': 'int',
      'ObjectId': 'str',
      'UUID': 'str',
      'ID': 'int',
      'Email': 'str',
      'URL': 'str',
      'Phone': 'str',
      'Currency': 'float',
      'Coordinates': 'Dict[str, float]',
      'Array': 'List[Any]',
      'Object': 'Dict[str, Any]'
    };

    return typeMap[type] || 'Any';
  }

  generateGo(): string {
    const structFields = this.analysis.fields.map(field => {
      const type = this.mapTypeToGo(field.type);
      const jsonTag = `json:"${field.name}"`;
      return `    ${this.capitalize(field.name)} ${type} \`${jsonTag}\``;
    }).join('\n');

    return `type DataItem struct {
${structFields}
}

type DataArray []DataItem`;
  }

  private mapTypeToGo(type: string): string {
    const typeMap: Record<string, string> = {
      'String': 'string',
      'Number': 'int',
      'Float': 'float64',
      'Boolean': 'bool',
      'Date': 'time.Time',
      'Timestamp': 'int64',
      'ObjectId': 'string',
      'UUID': 'string',
      'ID': 'int',
      'Email': 'string',
      'URL': 'string',
      'Phone': 'string',
      'Currency': 'float64',
      'Coordinates': 'map[string]float64',
      'Array': '[]interface{}',
      'Object': 'map[string]interface{}'
    };

    return typeMap[type] || 'interface{}';
  }

  private capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}

// =====================
// دوال المساعدة
// =====================

function parseCSV(csvText: string): any[] {
  const lines = csvText.trim().split('\n');
  if (lines.length === 0) return [];

  const headers = lines[0].split(',').map(header => header.trim().replace(/"/g, ''));
  const data: any[] = [];
  
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const values: string[] = [];
    let currentValue = '';
    let insideQuotes = false;
    
    for (let j = 0; j < line.length; j++) {
      const char = line[j];
      
      if (char === '"') {
        insideQuotes = !insideQuotes;
      } else if (char === ',' && !insideQuotes) {
        values.push(currentValue.trim());
        currentValue = '';
      } else {
        currentValue += char;
      }
    }
    
    values.push(currentValue.trim());

    const row: any = {};
    headers.forEach((header, index) => {
      let value = values[index] || '';
      value = value.replace(/^"|"$/g, '');
      
      if (!isNaN(Number(value)) && value !== '') {
        row[header] = Number(value);
      } else if (value.toLowerCase() === 'true' || value.toLowerCase() === 'false') {
        row[header] = value.toLowerCase() === 'true';
      } else {
        row[header] = value;
      }
    });
    
    data.push(row);
  }
  
  return data;
}

function parseData(dataText: string): any[] {
  const trimmedText = dataText.trim();
  
  try {
    return JSON.parse(trimmedText);
  } catch (jsonError) {
    try {
      return parseCSV(trimmedText);
    } catch (csvError) {
      throw new Error('لا يمكن تحليل البيانات. تأكد من أن الملف بصيغة JSON أو CSV صحيحة.');
    }
  }
}

export async function analyzeDataOptimized(
  dataText: string, 
  progressCallback?: (progress: number, stage: string) => void
): Promise<AnalysisResult> {
  let data: any[];
  
  try {
    data = parseData(dataText);
  } catch (error) {
    throw new Error('خطأ في تحليل البيانات: ' + (error as Error).message);
  }
  
  const analyzer = new OptimizedDataAnalyzer(data, progressCallback);
  return await analyzer.analyze();
}

export function generateCodeOptimized(analysis: AnalysisResult, language: string): string {
  const generator = new OptimizedCodeGenerator(analysis);
  
  switch (language.toLowerCase()) {
    case 'typescript':
      return generator.generateTypeScript();
    case 'sql':
      return generator.generateSQL();
    case 'python':
      return generator.generatePython();
    case 'go':
      return generator.generateGo();
    default:
      return generator.generateTypeScript();
  }
} 